import {Box, Button, CircularProgress} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {ExportField} from '../../interfaces/Export';
import {saveFile} from '../../utils/file';
import {CloseSnackbarAction} from './CloseSnackbarButton';
import ExportFieldForm from './ExportFieldForm';
import ModalFixed from './ModalFixed';
import SnackbarMessages from './SnackbarMessages';

type ExportStatus = 'idle' | 'creating' | 'processing' | 'ready' | 'downloading';

interface ExportPdfButtonProps {
  endpoint: string;
  defaultFilename: string;
  params?: Record<string, unknown>;
  additionalParams?: Record<string, unknown>;
  exportFields?: ExportField[];
}

const ExportPdfButton = ({
  endpoint,
  defaultFilename,
  params,
  additionalParams,
  exportFields,
}: ExportPdfButtonProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [jobId, setJobId] = useState<number | null>(null);
  const [progress, setProgress] = useState<{current: number; total: number}>({
    current: 0,
    total: 0,
  });
  const [isOpened, setIsOpened] = useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const isPollingActiveRef = useRef(false);

  const stopPolling = useCallback(() => {
    isPollingActiveRef.current = false;
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const downloadFile = useCallback(
    async (id: number) => {
      setStatus('downloading');
      try {
        const resp = await API.get(`${apiBaseUrl}/pdf/${id}/download`, {
          responseType: 'blob',
        });
        let filename = defaultFilename;
        const contentDisposition = resp?.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/);
          if (match?.[1]) {
            filename = match[1];
          }
        }
        saveFile(resp.data, filename);
      } catch (error: unknown) {
        const messages = getMessagesFromApiError(error, 'STRA_pdf_export');
        enqueueSnackbar(<SnackbarMessages messages={messages} />, {
          variant: 'error',
          action: CloseSnackbarAction,
        });
      }
      setStatus('idle');
      setJobId(null);
      setProgress({current: 0, total: 0});
    },
    [enqueueSnackbar, defaultFilename]
  );

  const pollJobStatus = useCallback(
    async (id: number) => {
      if (!isPollingActiveRef.current) {
        return;
      }

      try {
        const resp = await API.get(`${apiBaseUrl}/pdf/${id}/status`);
        const data = resp.data?.data;

        if (!isPollingActiveRef.current) {
          return;
        }

        if (data?.progress_current !== undefined && data?.progress_total !== undefined) {
          setProgress({
            current: data.progress_current,
            total: data.progress_total,
          });
        }

        if (data?.status === 'done') {
          stopPolling();
          setStatus('ready');
        } else if (data?.status === 'failed') {
          stopPolling();
          const errorMessage = data?.error || 'PDF generation failed';
          enqueueSnackbar(errorMessage, {
            variant: 'error',
            action: CloseSnackbarAction,
          });
          setStatus('idle');
          setJobId(null);
          setProgress({current: 0, total: 0});
        }
      } catch (error: unknown) {
        stopPolling();
        const messages = getMessagesFromApiError(error, 'STRA_pdf_export');
        enqueueSnackbar(<SnackbarMessages messages={messages} />, {
          variant: 'error',
          action: CloseSnackbarAction,
        });
        setStatus('idle');
        setJobId(null);
        setProgress({current: 0, total: 0});
      }
    },
    [stopPolling, enqueueSnackbar]
  );

  useEffect(() => {
    if (jobId && status === 'processing') {
      isPollingActiveRef.current = true;
      pollJobStatus(jobId);
      pollingIntervalRef.current = setInterval(() => {
        pollJobStatus(jobId);
      }, 1500);

      return () => {
        stopPolling();
      };
    }
  }, [jobId, status, pollJobStatus, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const fetchData = async (fields: string[]) => {
    setIsOpened(false);
    setStatus('creating');
    try {
      const requestParams = {
        ...params,
        ...additionalParams,
        export: 'pdf',
        fields,
      };
      const resp = await API.get(`${apiBaseUrl}${endpoint}`, {
        params: requestParams,
      });

      if (resp.data?.errors?.length) {
        enqueueSnackbar(<SnackbarMessages messages={resp.data.errors} />, {
          variant: 'error',
          action: CloseSnackbarAction,
        });
        setStatus('idle');
        return;
      }

      if (resp.data?.success && resp.data?.job_id) {
        setJobId(resp.data.job_id);
        setStatus('processing');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: unknown) {
      const messages = getMessagesFromApiError(error, 'STRA_pdf_export');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
      setStatus('idle');
    }
  };

  const handleDownloadClick = () => {
    if (status === 'ready' && jobId) {
      downloadFile(jobId);
    }
  };

  const showSideButton = status !== 'idle';
  const isProcessing = status === 'creating' || status === 'processing' || status === 'downloading';
  const progressPercent = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  const renderSideButtonContent = () => {
    if (status === 'creating') {
      return (
        <>
          <CircularProgress size={20} sx={{mr: 1}} />
          Creating...
        </>
      );
    }
    if (status === 'processing') {
      if (progress.total > 0) {
        return (
          <>
            <CircularProgress size={20} variant="determinate" value={progressPercent} sx={{mr: 1}} />
            {Math.round(progressPercent)}%
          </>
        );
      }
      return (
        <>
          <CircularProgress size={20} sx={{mr: 1}} />
          Processing...
        </>
      );
    }
    if (status === 'downloading') {
      return (
        <>
          <CircularProgress size={20} sx={{mr: 1}} />
          Downloading...
        </>
      );
    }
    if (status === 'ready') {
      return 'Download';
    }
    return null;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="end" gap={1}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={() => setIsOpened(true)}
        >
          Export to PDF
        </Button>
        {showSideButton && (
          <Button
            size="large"
            variant="outlined"
            disabled={isProcessing}
            onClick={handleDownloadClick}
            sx={{minWidth: 180}}
          >
            {renderSideButtonContent()}
          </Button>
        )}
      </Box>
      <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <ExportFieldForm
            exportFields={exportFields}
            loading={false}
            onChange={fetchData}
            close={() => setIsOpened(false)}
          />
        </Box>
      </ModalFixed>
    </Box>
  );
};

export default ExportPdfButton;
